import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  VaultResponseDto,
  VaultCreateDto,
  VaultUpdateDto,
  VaultDeleteDto,
} from '../../types/api';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../config/api';

export type VaultItem = VaultResponseDto;

export interface VaultState {
  loading: boolean;
  items: VaultItem[];
  error: string | null;
  selectedVaultId: string | null;
  isModalOpen: boolean;
  modalMode: 'add' | 'edit';
  operationLoading: {
    list: boolean;
    get: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  errors: {
    list: string | null;
    get: string | null;
    create: string | null;
    update: string | null;
    delete: string | null;
  };
}

const initialOperationLoading = {
  list: false,
  get: false,
  create: false,
  update: false,
  delete: false,
};

const initialErrors = {
  list: null as string | null,
  get: null as string | null,
  create: null as string | null,
  update: null as string | null,
  delete: null as string | null,
};

const initialState: VaultState = {
  loading: false,
  items: [],
  error: null,
  selectedVaultId: null,
  isModalOpen: false,
  modalMode: 'add',
  operationLoading: { ...initialOperationLoading },
  errors: { ...initialErrors },
};

export const getVaults = createAsyncThunk(
  'vault/getVaults',
  async (_, { rejectWithValue }) => {
    try {
      return await apiGet<VaultItem[]>('/api/Vault');
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Kasa listesi yüklenirken hata oluştu');
    }
  }
);

export const getVault = createAsyncThunk(
  'vault/getVault',
  async (id: string, { rejectWithValue }) => {
    try {
      return await apiGet<VaultItem>(`/api/Vault/${id}`);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Kasa yüklenirken hata oluştu');
    }
  }
);

export const createVault = createAsyncThunk(
  'vault/createVault',
  async (body: VaultCreateDto, { rejectWithValue }) => {
    try {
      return await apiPost<VaultItem>('/api/Vault', body);
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Kasa oluşturulurken hata oluştu');
    }
  }
);

/** Update vault (e.g. rename). Requires ownerId as query param (Swagger). */
export const updateVault = createAsyncThunk(
  'vault/updateVault',
  async (
    { id, ownerId, body }: { id: string; ownerId: string; body: VaultUpdateDto },
    { rejectWithValue }
  ) => {
    try {
      const path = `/api/Vault/${id}?ownerId=${encodeURIComponent(ownerId)}`;
      const item = await apiPatch<VaultItem>(path, body);
      return { id, item };
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Kasa güncellenirken hata oluştu');
    }
  }
);

/** Delete vault. Swagger: query ownerId + optional body VaultDeleteDto (vaultMasterPassword). */
export const deleteVault = createAsyncThunk(
  'vault/deleteVault',
  async (
    { id, ownerId, body }: { id: string; ownerId: string; body?: VaultDeleteDto },
    { rejectWithValue }
  ) => {
    try {
      const path = `/api/Vault/${id}?ownerId=${encodeURIComponent(ownerId)}`;
      await apiDelete(path, body ?? undefined);
      return id;
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : 'Kasa silinirken hata oluştu');
    }
  }
);

const vaultSlice = createSlice({
  name: 'vault',
  initialState,
  reducers: {
    setSelectedVaultId: (state, action: PayloadAction<string | null>) => {
      state.selectedVaultId = action.payload;
    },
    setVaultModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isModalOpen = action.payload;
    },
    setVaultModalMode: (state, action: PayloadAction<'add' | 'edit'>) => {
      state.modalMode = action.payload;
    },
    openVaultEditModal: (state, action: PayloadAction<string>) => {
      state.selectedVaultId = action.payload;
      state.modalMode = 'edit';
      state.isModalOpen = true;
    },
    openVaultAddModal: (state) => {
      state.selectedVaultId = null;
      state.modalMode = 'add';
      state.isModalOpen = true;
    },
    resetVaultSelection: (state) => {
      state.selectedVaultId = null;
      state.isModalOpen = false;
      state.modalMode = 'add';
    },
    clearVaultError: (state) => {
      state.error = null;
      state.errors = { ...initialErrors };
    },
    clearVaultItems: (state) => {
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getVaults.pending, (state) => {
      state.operationLoading.list = true;
      state.error = null;
      state.errors.list = null;
    });
    builder.addCase(getVaults.fulfilled, (state, action) => {
      state.operationLoading.list = false;
      state.items = Array.isArray(action.payload) ? action.payload : [];
      state.errors.list = null;
    });
    builder.addCase(getVaults.rejected, (state, action) => {
      state.operationLoading.list = false;
      const msg = typeof action.payload === 'string' ? action.payload : 'Kasa listesi yüklenirken hata oluştu';
      state.error = msg;
      state.errors.list = msg;
    });

    builder.addCase(getVault.pending, (state) => {
      state.operationLoading.get = true;
      state.errors.get = null;
    });
    builder.addCase(getVault.fulfilled, (state, action) => {
      state.operationLoading.get = false;
      const item = action.payload;
      const idx = state.items.findIndex((i) => i.id === item.id);
      if (idx >= 0) state.items[idx] = item;
      else state.items.push(item);
      state.errors.get = null;
    });
    builder.addCase(getVault.rejected, (state, action) => {
      state.operationLoading.get = false;
      state.errors.get = typeof action.payload === 'string' ? action.payload : 'Kasa yüklenirken hata oluştu';
    });

    builder.addCase(createVault.pending, (state) => {
      state.operationLoading.create = true;
      state.errors.create = null;
    });
    builder.addCase(createVault.fulfilled, (state, action) => {
      state.operationLoading.create = false;
      const item = action.payload;
      if (item?.id && !state.items.some((i) => i.id === item.id)) state.items.push(item);
      state.errors.create = null;
    });
    builder.addCase(createVault.rejected, (state, action) => {
      state.operationLoading.create = false;
      state.errors.create = typeof action.payload === 'string' ? action.payload : 'Kasa oluşturulurken hata oluştu';
    });

    builder.addCase(updateVault.pending, (state) => {
      state.operationLoading.update = true;
      state.errors.update = null;
    });
    builder.addCase(updateVault.fulfilled, (state, action) => {
      state.operationLoading.update = false;
      const { id, item } = action.payload;
      const idx = state.items.findIndex((i) => i.id === id);
      if (idx >= 0 && item) state.items[idx] = item;
      state.errors.update = null;
    });
    builder.addCase(updateVault.rejected, (state, action) => {
      state.operationLoading.update = false;
      state.errors.update = typeof action.payload === 'string' ? action.payload : 'Kasa güncellenirken hata oluştu';
    });

    builder.addCase(deleteVault.pending, (state) => {
      state.operationLoading.delete = true;
      state.errors.delete = null;
    });
    builder.addCase(deleteVault.fulfilled, (state, action) => {
      state.operationLoading.delete = false;
      state.items = state.items.filter((i) => i.id !== action.payload);
      state.errors.delete = null;
    });
    builder.addCase(deleteVault.rejected, (state, action) => {
      state.operationLoading.delete = false;
      state.errors.delete = typeof action.payload === 'string' ? action.payload : 'Kasa silinirken hata oluştu';
    });
  },
});

export const {
  setSelectedVaultId,
  setVaultModalOpen,
  setVaultModalMode,
  openVaultEditModal,
  openVaultAddModal,
  resetVaultSelection,
  clearVaultError,
  clearVaultItems,
} = vaultSlice.actions;
export default vaultSlice.reducer;
